import React, { useState, useEffect, useMemo } from 'react';
import patternsRadarService from '../../../services/patternsRadarService';
import DoubleScrollTable from './DoubleScrollTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookmark,
  faBell,
  faTrash,
  faEdit,
  faPlay,
  faSpinner,
  faEnvelope,
  faGlobe,
  faHistory,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

const SavedScansAlerts = ({ onRunScan }) => {
  const [scans, setScans] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Edit scan state
  const [editingScan, setEditingScan] = useState(null);
  const [editName, setEditName] = useState('');

  // Alert Config Modal
  const [configuringAlert, setConfiguringAlert] = useState(null);
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [triggerMode, setTriggerMode] = useState('new_matches');
  const [channelEmail, setChannelEmail] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [savingAlert, setSavingAlert] = useState(false);

  // Alert History & Delivery Logs
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [selectedScanForEvents, setSelectedScanForEvents] = useState(null);
  const [alertEvents, setAlertEvents] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const [defaultWebhookUrl, setDefaultWebhookUrl] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scansData, alertsData, configData] = await Promise.all([
        patternsRadarService.listSavedScans().catch(() => ({ scans: [] })),
        patternsRadarService.listAlerts().catch(() => ({ alerts: [] })),
        patternsRadarService.getConfig().catch(() => null),
      ]);
      setScans(scansData.scans || []);
      setAlerts(alertsData.alerts || []);
      if (configData?.webhook_url) {
        setDefaultWebhookUrl(configData.webhook_url);
      }
    } catch (err) {
      console.error('Error fetching scans & alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Table Column Filters (CRM Theme)
  const [colFilterName, setColFilterName] = useState('');
  const [colFilterQuery, setColFilterQuery] = useState('');
  const [colFilterUniverse, setColFilterUniverse] = useState('');
  const [colFilterStatus, setColFilterStatus] = useState('');

  const hasActiveFilters = Boolean(
    colFilterName || colFilterQuery || colFilterUniverse || colFilterStatus
  );

  const clearFilters = () => {
    setColFilterName('');
    setColFilterQuery('');
    setColFilterUniverse('');
    setColFilterStatus('');
  };

  const filteredScans = useMemo(() => {
    return scans.filter((scan) => {
      if (
        colFilterName &&
        !scan.name?.toLowerCase().includes(colFilterName.toLowerCase().trim())
      ) {
        return false;
      }
      if (
        colFilterQuery &&
        !scan.source?.toLowerCase().includes(colFilterQuery.toLowerCase().trim())
      ) {
        return false;
      }
      if (
        colFilterUniverse &&
        String(scan.universe || 500) !== String(colFilterUniverse)
      ) {
        return false;
      }
      if (colFilterStatus) {
        const alert = alerts.find((a) => a.scan_id === scan.id);
        if (colFilterStatus === 'active' && (!alert || !alert.enabled)) return false;
        if (colFilterStatus === 'paused' && (!alert || alert.enabled)) return false;
        if (colFilterStatus === 'none' && alert) return false;
      }
      return true;
    });
  }, [scans, alerts, colFilterName, colFilterQuery, colFilterUniverse, colFilterStatus]);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);

  useEffect(() => {
    setPage(1);
  }, [filteredScans.length]);

  const totalPages = Math.ceil(filteredScans.length / pageSize) || 1;
  const start = filteredScans.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filteredScans.length);

  const paginatedScans = useMemo(() => {
    return filteredScans.slice((page - 1) * pageSize, page * pageSize);
  }, [filteredScans, page, pageSize]);

  const handleDeleteScan = async (id) => {
    if (!window.confirm('Are you sure you want to delete this saved scan?'))
      return;
    try {
      await patternsRadarService.deleteScan(id);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete scan.');
    }
  };

  const handleRenameScan = async () => {
    if (!editName.trim() || !editingScan) return;
    try {
      await patternsRadarService.renameScan(editingScan.id, editName.trim());
      setEditingScan(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to rename scan.');
    }
  };

  const handleOpenAlertConfig = (scan) => {
    const existing = alerts.find((a) => a.scan_id === scan.id);
    setConfiguringAlert(scan);
    if (existing) {
      setAlertEnabled(existing.enabled);
      setTriggerMode(existing.trigger_mode || 'new_matches');
      setChannelEmail(existing.channel_email ?? true);
      setWebhookUrl(existing.webhook_url || defaultWebhookUrl || '');
    } else {
      setAlertEnabled(true);
      setTriggerMode('new_matches');
      setChannelEmail(true);
      setWebhookUrl(defaultWebhookUrl || '');
    }
  };

  const handleSaveAlert = async () => {
    if (!configuringAlert) return;
    setSavingAlert(true);
    try {
      const payload = {
        enabled: alertEnabled,
        trigger_mode: triggerMode,
        channel_email: channelEmail,
      };
      if (webhookUrl.trim()) {
        payload.webhook_url = webhookUrl.trim();
      }

      const res = await patternsRadarService.setAlert(configuringAlert.id, payload);
      const secret = res?.webhook_secret || res?.secret;
      if (secret) {
        alert(
          `Alert configured successfully!\n\nWebhook secret: ${secret}\n(This secret has been automatically stored in tbl_configuration in your database.)`
        );
      }
      setConfiguringAlert(null);
      fetchData();
    } catch (err) {
      alert(
        err.response?.data?.detail ||
          err.response?.data?.title ||
          'Failed to configure alert.'
      );
    } finally {
      setSavingAlert(false);
    }
  };

  const handleRemoveAlert = async (scanId) => {
    if (!window.confirm('Detach alert from this scan?')) return;
    try {
      await patternsRadarService.removeAlert(scanId);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove alert.');
    }
  };

  const handleViewEvents = async (scan) => {
    setSelectedScanForEvents(scan);
    setShowEventsModal(true);
    setEventsLoading(true);
    try {
      const [eventsRes, deliveriesRes] = await Promise.all([
        patternsRadarService
          .getAlertEvents({ scan_id: scan.id, limit: 10 })
          .catch(() => ({ events: [] })),
        patternsRadarService
          .getWebhookDeliveries(scan.id, 10)
          .catch(() => ({ deliveries: [] })),
      ]);
      setAlertEvents(eventsRes.events || []);
      setDeliveries(deliveriesRes.deliveries || []);
    } catch (err) {
      console.error('Error fetching event logs:', err);
    } finally {
      setEventsLoading(false);
    }
  };

  return (
    <div>
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
          <span className="panel-title font-weight-bold">
            <FontAwesomeIcon icon={faBookmark} className="text-primary me-2" />
            Saved Scans Library & Nightly Alert Engine
          </span>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {filteredScans.length > 0 && (
              <>
                <span className="small text-muted">
                  Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{filteredScans.length}</strong>
                </span>
                {filteredScans.length > 100 && (
                  <div className="pagination-nav-buttons ms-1">
                    <button
                      type="button"
                      className="btn btn-default btn-xs nav-prev-btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      title="Previous"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-default btn-xs nav-next-btn"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      title="Next"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}
              </>
            )}
            <button
              className="btn btn-default btn-xs"
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="panel-body p-0">
          <DoubleScrollTable dependencies={[paginatedScans, alerts]}>
            <table className="table table-hover table-condensed bg-white m-b-0 sticky-table pr-table">
              <thead>
                <tr className="bg-light">
                  <th style={{ minWidth: '180px' }}>Scan Name</th>
                  <th style={{ minWidth: '220px' }}>Sift Query Expression</th>
                  <th style={{ minWidth: '110px' }} className="text-center">Universe</th>
                  <th style={{ minWidth: '120px' }} className="text-center">Alert Status</th>
                  <th style={{ minWidth: '130px' }} className="text-center">Channels</th>
                  <th style={{ minWidth: '130px' }} className="text-center">Last Evaluated</th>
                  <th style={{ minWidth: '140px' }} className="text-center">Actions</th>
                </tr>
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder="Filter Name..."
                      value={colFilterName}
                      onChange={(e) => setColFilterName(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Filter Query..."
                      value={colFilterQuery}
                      onChange={(e) => setColFilterQuery(e.target.value)}
                    />
                  </th>
                  <th>
                    <select
                      value={colFilterUniverse}
                      onChange={(e) => setColFilterUniverse(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="100">Top 100</option>
                      <option value="500">Top 500</option>
                      <option value="2000">Top 2000</option>
                      <option value="9999">Full DB</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={colFilterStatus}
                      onChange={(e) => setColFilterStatus(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="none">No Alert</option>
                    </select>
                  </th>
                  <th></th>
                  <th></th>
                  <th className="text-center">
                    {hasActiveFilters ? (
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-clear-filter"
                        onClick={clearFilters}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        className="me-2 text-primary"
                      />
                      Loading saved scans & active alert monitors...
                    </td>
                  </tr>
                ) : paginatedScans.length > 0 ? (
                  paginatedScans.map((scan) => {
                    const alert = alerts.find((a) => a.scan_id === scan.id);
                    return (
                      <tr key={scan.id}>
                        <td className="font-weight-bold">
                          {scan.name}
                          <small className="text-muted d-block font-weight-normal">
                            ID: #{scan.id} | Updated:{' '}
                            {scan.updated_at
                              ? new Date(scan.updated_at).toLocaleDateString()
                              : '-'}
                          </small>
                        </td>
                        <td
                          className="text-truncate font-monospace small"
                          style={{ maxWidth: '300px' }}
                          title={scan.source}
                        >
                          {scan.source}
                        </td>
                        <td className="text-center">
                          <span className="badge bg-secondary">
                            Top {scan.universe || 500}
                          </span>
                        </td>
                        <td className="text-center">
                          {alert ? (
                            <span
                              className={`badge ${
                                alert.enabled ? 'bg-success' : 'bg-warning text-dark'
                              }`}
                            >
                              {alert.enabled ? 'ACTIVE' : 'PAUSED'}
                            </span>
                          ) : (
                            <span className="badge bg-light text-muted border">
                              NO ALERT
                            </span>
                          )}
                        </td>
                        <td className="text-center">
                          {alert ? (
                            <div className="d-flex justify-content-center gap-1">
                              {alert.channel_email && (
                                <span
                                  className="badge bg-info text-white"
                                  title="Email Alerts On"
                                >
                                  <FontAwesomeIcon icon={faEnvelope} />
                                </span>
                              )}
                              {alert.webhook_url && (
                                <span
                                  className="badge bg-dark text-white"
                                  title={`Webhook: ${alert.webhook_url}`}
                                >
                                  <FontAwesomeIcon icon={faGlobe} />
                                </span>
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="text-center small text-muted">
                          {alert?.last_evaluated
                            ? new Date(alert.last_evaluated).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="text-center">
                          <div className="d-inline-flex gap-1 justify-content-center align-items-center">
                            <button
                              className="btn btn-primary btn-xs"
                              onClick={() => onRunScan && onRunScan(scan.source)}
                              title="Run in Screener Studio"
                            >
                              <FontAwesomeIcon icon={faPlay} />
                            </button>
                            <button
                              className={`btn btn-xs ${
                                alert ? 'btn-info' : 'btn-default'
                              }`}
                              onClick={() => handleOpenAlertConfig(scan)}
                              title="Configure Alert"
                            >
                              <FontAwesomeIcon icon={faBell} />
                            </button>
                            {alert && (
                              <button
                                className="btn btn-secondary btn-xs"
                                onClick={() => handleViewEvents(scan)}
                                title="View Nightly Audit Logs"
                              >
                                <FontAwesomeIcon icon={faHistory} />
                              </button>
                            )}
                            <button
                              className="btn btn-warning btn-xs"
                              onClick={() => {
                                setEditingScan(scan);
                                setEditName(scan.name);
                              }}
                              title="Rename Scan"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => handleDeleteScan(scan.id)}
                              title="Delete Scan"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      {hasActiveFilters
                        ? 'No saved scans match the active filters. (Click "Clear" to reset filters)'
                        : 'No saved scans found. Save your queries from Screener Studio!'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DoubleScrollTable>

          {/* Footer Pagination Bar */}
          {filteredScans.length > 100 && (
            <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light flex-wrap gap-2">
              <div className="small text-muted">
                Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{filteredScans.length}</strong> saved scans
              </div>
              <div className="pagination-nav-buttons">
                <button
                  type="button"
                  className="btn btn-default btn-xs nav-prev-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  title="Previous"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  type="button"
                  className="btn btn-default btn-xs nav-next-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  title="Next"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Configure Alert Modal */}
      {configuringAlert && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title font-weight-bold">
                  <FontAwesomeIcon icon={faBell} className="text-primary me-2" />
                  Configure Post-Market Alert: {configuringAlert.name}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfiguringAlert(null)}
                />
              </div>
              <div className="modal-body">
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="alertActiveSwitch"
                    checked={alertEnabled}
                    onChange={(e) => setAlertEnabled(e.target.checked)}
                  />
                  <label
                    className="form-check-label font-weight-bold"
                    htmlFor="alertActiveSwitch"
                  >
                    Enable Automated Nightly Evaluation
                  </label>
                </div>

                <div className="mb-3">
                  <label className="form-label small font-weight-bold">
                    Trigger Mode
                  </label>
                  <select
                    className="form-control form-control-sm"
                    value={triggerMode}
                    onChange={(e) => setTriggerMode(e.target.value)}
                  >
                    <option value="new_matches">
                      New Matches Only (Notify when new stocks trigger signal)
                    </option>
                    <option value="any_match">
                      Any Match (Notify whenever any stock matches)
                    </option>
                  </select>
                </div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="channelEmailCheck"
                    checked={channelEmail}
                    onChange={(e) => setChannelEmail(e.target.checked)}
                  />
                  <label
                    className="form-check-label small font-weight-bold"
                    htmlFor="channelEmailCheck"
                  >
                    Send Notification via Registered Email
                  </label>
                </div>

                <div className="mb-2">
                  <label className="form-label small font-weight-bold">
                    Webhook Destination URL (Optional / Max Plan)
                  </label>
                  <input
                    type="url"
                    className="form-control form-control-sm"
                    placeholder="https://yourcrm.com/api/webhooks/street-analysis"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                  />
                  <small className="text-muted">
                    Street Analysis delivers webhook events with HMAC-SHA256
                    signatures.
                  </small>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveAlert(configuringAlert.id)}
                >
                  Detach Alert
                </button>
                <div>
                  <button
                    className="btn btn-default btn-sm me-2"
                    onClick={() => setConfiguringAlert(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveAlert}
                    disabled={savingAlert}
                  >
                    {savingAlert ? 'Saving...' : 'Save Alert'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rename Scan Modal */}
      {editingScan && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title font-weight-bold">Rename Scan</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingScan(null)}
                />
              </div>
              <div className="modal-body">
                <label className="form-label small font-weight-bold">
                  New Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={120}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-default btn-sm"
                  onClick={() => setEditingScan(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleRenameScan}
                >
                  Update Name
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log / Events Drawer Modal */}
      {showEventsModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title font-weight-bold">
                  Alert Audit & Diagnostics: {selectedScanForEvents?.name}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEventsModal(false)}
                />
              </div>
              <div className="modal-body p-3">
                <h6 className="font-weight-bold small text-muted text-uppercase mb-2">
                  Nightly Evaluation History (/v1/alerts/events)
                </h6>
                {eventsLoading ? (
                  <div className="text-center py-3">
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Loading event history...
                  </div>
                ) : alertEvents.length > 0 ? (
                  <DoubleScrollTable className="mb-3" dependencies={[alertEvents]}>
                    <table className="table table-bordered table-condensed bg-white small mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>All Matches</th>
                          <th>New Matches</th>
                          <th>Notification Fired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alertEvents.map((ev, idx) => (
                          <tr key={idx}>
                            <td>{ev.data_date || '-'}</td>
                            <td>{ev.matched?.join(', ') || 'None'}</td>
                            <td>
                              <strong className="text-success">
                                {ev.new_matches?.join(', ') || 'None'}
                              </strong>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  ev.fired ? 'bg-success' : 'bg-light text-muted'
                                }`}
                              >
                                {ev.fired ? 'FIRED' : 'QUIET'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DoubleScrollTable>
                ) : (
                  <div className="small text-muted mb-3">
                    No evaluated events found yet for this scan.
                  </div>
                )}

                {deliveries.length > 0 && (
                  <>
                    <h6 className="font-weight-bold small text-muted text-uppercase mb-2">
                      Webhook Deliveries (/v1/alerts/:id/deliveries)
                    </h6>
                    <DoubleScrollTable dependencies={[deliveries]}>
                      <table className="table table-bordered table-condensed bg-white small mb-0">
                        <thead>
                          <tr>
                            <th>Attempt</th>
                            <th>Status Code</th>
                            <th>Duration (ms)</th>
                            <th>Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveries.map((d, idx) => (
                            <tr key={idx}>
                              <td>#{d.attempt}</td>
                              <td>{d.status_code || '-'}</td>
                              <td>{d.duration_ms}ms</td>
                              <td>
                                <span
                                  className={`badge ${
                                    d.ok ? 'bg-success' : 'bg-danger'
                                  }`}
                                >
                                  {d.ok ? 'OK 200' : d.error || 'FAILED'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </DoubleScrollTable>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-default btn-sm"
                  onClick={() => setShowEventsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedScansAlerts;
